import { createSelector } from 'reselect'
import truncate from 'lodash/truncate'
import pick from 'lodash/pick'
import { stringifyURLParams } from '@offerzen/pinata-shame'
import { formatSmartTimestamp, formatSmartDate } from '@offerzen/utils'

import { getInterviewRequestMap } from '@/core/reducks/interviewRequests'
import { getMemberProfileMap } from '@/core/reducks/memberProfiles'
import { getPositionMap } from '@/core/reducks/positions'
import { memberProfileId } from '@/core/selectors/currentUser'
import { urlParams, urlPath } from '@/core/selectors/router'

import { interviewRequestStatusMap, interviewRequestAtsStatusMap } from '../helpers/interviewRequestHelpers'
import { getInterviewRequestListView } from '../reducks/interviewRequestList'
import { getUnavailableCandidateNotificationStatus as getNotificationMessage } from '../helpers/unavailableCandidateNotificationsHelpers'

export const interviewRequestListFilters = createSelector([urlParams], ({ order_by, page, ...params }) => ({
  current_hiring_team_id: params.current_hiring_team_id || undefined,
  archived: params.archived === 'true' || undefined,
  member_profile_id: params.member_profile_id,
  status: params.status || undefined,
  position_ids: params.position_ids || [],
  order_by,
  order_direction: order_by ? params.order_direction : undefined,
  page: page === 1 ? undefined : page,
  messages: params.messages || 'all',
  candidate_name: params.candidate_name || undefined,
}))

export const interviewRequestFilterAnalytics = createSelector([urlParams], params => ({
  archived: params.archived === 'true' || false,
  memberId: params.member_profile_id || 'all',
  atsStatus: params.status || 'all',
  positionIds: params.position_ids || [],
  messageStatus: params.messages || 'all',
  candidateName: params.candidate_name || 'none',
}))

export const interviewRequestListFilterCount = createSelector([memberProfileId, urlParams], (currentMemberId, params) => {
  let count = 0

  if (params.member_profile_id !== currentMemberId) count++
  if (params.status) count++
  if (params.position_ids && params.position_ids.length > 0) count++
  if (params.messages && params.messages !== 'all') count++
  if (params.candidate_name) count++

  return count
})

// Make sure the statuses are in the desired order
export const interviewRequestStatusOptions = () => [
  interviewRequestStatusMap.pending,
  interviewRequestStatusMap.accepted,
  interviewRequestAtsStatusMap.interviewing,
  interviewRequestAtsStatusMap.technical_screening,
  interviewRequestAtsStatusMap.extending_offer,
  interviewRequestAtsStatusMap.hired,
  {
    ...interviewRequestAtsStatusMap.rejected,
    label: 'Rejected (by you)',
  },
  {
    ...interviewRequestStatusMap.declined,
    label: 'Declined (by candidate)',
  },
]

export const positionOptions = createSelector([getInterviewRequestListView, getPositionMap], ({ positions }, positionsMap) =>
  positions.map(positionId => {
    const { name } = positionsMap[positionId]
    return {
      id: positionId,
      label: name,
    }
  }),
)

export const memberOptions = createSelector(
  [memberProfileId, getInterviewRequestListView, getMemberProfileMap],
  (currentMemberId, { members }, memberMap) => {
    return [
      { id: currentMemberId, label: 'Your messages' },
      { id: 'all', label: 'All members' },
      ...members
        .filter(memberId => memberId !== currentMemberId)
        .map(memberId => {
          const { name } = memberMap[memberId]
          return { id: memberId, label: name }
        }),
    ]
  },
)

export const companyHasInterviewRequests = createSelector(
  [getInterviewRequestListView],
  view => !view.fetchingInterviewRequests && !!view.lastUpdatedInterviewRequests && view.interviewRequestsOpenHiringTeam !== 0,
)

export const clearFiltersEnabled = createSelector(
  [memberProfileId, urlParams],
  (currentMemberId, params) =>
    params.member_profile_id !== currentMemberId ||
    !!params.status ||
    (!!params.position_ids && params.position_ids.length > 0) ||
    params.messages !== 'all' ||
    !!params.candidate_name,
)

export const loadingInterviewRequests = createSelector(
  [getInterviewRequestListView],
  view => view.fetchingInterviewRequests || !view.lastUpdatedInterviewRequests,
)

export const interviewRequestList = createSelector(
  [urlPath, urlParams, memberProfileId, getInterviewRequestListView, getInterviewRequestMap, interviewRequestStatusOptions],
  (
    pathname,
    params,
    currentMemberId,
    { interviewRequests, interviewRequestsStats },
    interviewRequestMap,
    interviewRequestStatusOptions,
  ) => {
    const statusOptions = [...interviewRequestStatusOptions, interviewRequestStatusMap.withdrawn]

    const impersonatedHiringTeamParam = pick(params, ['current_hiring_team_id'])
    const stringifiedHiringTeamParam = stringifyURLParams(impersonatedHiringTeamParam, { addQueryPrefix: true })

    return interviewRequests.map(interviewRequestId => {
      const { candidate_profile, last_message, position, ...interviewRequest } = interviewRequestMap[interviewRequestId]
      const status = interviewRequest.ats_status || interviewRequest.status

      let positionName = position && truncate(position.name, { length: 28 })
      if (position && position.archived) {
        positionName = positionName.concat(' (closed)')
      }

      return {
        id: interviewRequestId,
        href: `${pathname}/${interviewRequestId}${stringifiedHiringTeamParam}`,
        mobileHref: `${pathname}/${interviewRequestId}/messages`,
        candidateName: candidate_profile.name,
        candidateAvatar: interviewRequest.is_candidate_visible_to_company ? candidate_profile.profile_image_url : null,
        positionName: positionName || '-',
        ...(last_message
          ? {
              unread: !last_message.read_by.includes(currentMemberId),
              outgoingMessage: last_message.created_by !== candidate_profile.id,
              message: truncate(last_message.text, { length: 35 }),
              sentAt: formatSmartTimestamp(last_message.created_at),
              sentAtShort: formatSmartDate(last_message.created_at),
            }
          : {
              unread: false,
              outgoingMessage: true,
              message: truncate(interviewRequest.message, { length: 35 }),
              sentAt: formatSmartTimestamp(interviewRequest.created_at),
              sentAtShort: formatSmartDate(interviewRequest.created_at),
            }),
        status,
        statusLabel: statusOptions.find(elem => elem.id === status).label,
        archived: interviewRequest.archived,
        actioning: interviewRequestsStats[interviewRequestId].archiving,
        unavailableReason: interviewRequest.unavailable_reason ? getNotificationMessage(interviewRequest.unavailable_reason) : null,
        unavailableReasonShort: interviewRequest.unavailable_reason,
      }
    })
  },
)

export const interviewRequestPagination = createSelector([getInterviewRequestListView], ({ interviewRequestPagination }) => ({
  totalItems: interviewRequestPagination.total_items,
  currentPage: interviewRequestPagination.current_page,
  totalPages: interviewRequestPagination.total_pages,
}))

export const interviewRequestToWithdraw = createSelector(
  [getInterviewRequestListView, getInterviewRequestMap],
  ({ interviewRequestToWithdraw, interviewRequestsStats }, interviewRequestMap) => {
    if (!interviewRequestToWithdraw) return null

    const { candidate_profile } = interviewRequestMap[interviewRequestToWithdraw]
    const { withdrawing } = interviewRequestsStats[interviewRequestToWithdraw] || {}

    return {
      id: interviewRequestToWithdraw,
      avatar: candidate_profile.profile_image_url,
      name: candidate_profile.name,
      withdrawing,
    }
  },
)
